import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { api, ApiError, unwrap } from './client';
import { qk } from './query-keys';
import { gcTimeFor, staleTimeFor, type Freq } from './stale';
import type { components } from './schema';
import { shiftDays } from '@/lib/asof/asof';
import { clearToken, getPendingMfaToken, setPendingMfaToken, setToken } from '@/lib/auth/token-store';
import type { Market } from '@/lib/market/types';

/**
 * =============================================================================
 *  queryOptions —— 每一個資料查詢的**唯一**入口。
 *
 *  為什麼是 queryOptions 而不是散在元件裡的 useQuery：
 *  key、queryFn、staleTime 三者必須一起決定。分開寫的話，
 *  「key 帶了 as_of 但 queryFn 忘了把 as_of 送出去」是一個編譯得過、
 *  跑得動、而且畫面完全正常的 bug——它會安靜地把今天的數字貼在倒帶的畫面上。
 *  綁在一起，這個 bug 就沒有地方可以長。
 * ========================================================================== */

// 型別名一律跟著 live spec 走。API builder 把 MetricDef 取名為 MetricDefOut，
// 前端就叫 MetricDefOut——在這裡「修正」成自己喜歡的名字，等於又開了一個會漂移的對應表。
export type MetricDef = components['schemas']['MetricDefOut'];
export type MetricRegistryOut = components['schemas']['MetricRegistryOut'];
export type OhlcvOut = components['schemas']['OhlcvOut'];
export type SeriesOut = components['schemas']['SeriesOut'];
export type StatusOut = components['schemas']['StatusOut'];
export type AdjustMode = components['schemas']['AdjustMode'];

export interface DateRange {
  from: string;
  to: string;
}

/** as_of 是 undefined 時**不送**這個 query param——空字串與缺席在後端不是同一件事。 */
function asOfParam(asOf: string | undefined): { as_of?: string } {
  return asOf === undefined ? {} : { as_of: asOf };
}

export function statusQuery(asOf: string | undefined, today: string) {
  return queryOptions({
    queryKey: qk.status(asOf),
    queryFn: async ({ signal }) =>
      unwrap(await api.GET('/api/v1/status', { signal })),
    // /status 講的是「資料更新到什麼時候」，它本身永遠是即時的：
    // 倒帶不該凍結維運畫面，否則你會盯著一張 2015 年的健康表等今天的 job。
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    // today 只用來保持簽名一致；狀態頁刻意不隨 as_of 改變行為。
    meta: { today },
  });
}
export function sectorRotationQuery(market: Market, asOf: string | undefined) {
  return queryOptions({
    queryKey: qk.sectorRotation(market, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/market/sector-rotation', {
          params: { path: { market }, query: asOf !== undefined ? { date: asOf } : {} },
          signal,
        })
      ),
    staleTime: 60 * 1000,
  });
}
export function sectorOutlookQuery(market: Market, asOf: string | undefined) {
  return queryOptions({
    queryKey: qk.sectorOutlook(market, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/market/sector-rotation/outlook', {
          params: { path: { market }, query: asOf !== undefined ? { date: asOf } : {} },
          signal,
        })
      ),
    staleTime: 60 * 1000,
  });
}
export function sectorFlowQuery(market: Market, asOf: string | undefined, day?: string) {
  const date = day ?? asOf;
  return queryOptions({
    queryKey: qk.sectorFlow(market, asOf, day),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/market/sector-flow', {
          params: { path: { market }, query: date !== undefined ? { date } : {} },
          signal,
        }),
      ),
    staleTime: 60 * 1000,
  });
}
export function sectorConstituentsQuery(market: Market, industryCode: string, asOf: string | undefined) {
  return queryOptions({
    queryKey: qk.sectorConstituents(market, industryCode, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/market/sector-rotation/{industry_code}/constituents', {
          params: {
            path: { market, industry_code: industryCode },
            query: asOf !== undefined ? { date: asOf } : {},
          },
          signal,
        })
      ),
    staleTime: 60 * 1000,
  });
}
export type PremarketOut = components['schemas']['PremarketOut'];
export function premarketQuery(market: Market, asOf: string | undefined, date: string | undefined) {
  // 「今天」以 ?date 為準，其次是時光機位置；都沒有就讓後端取最新一個交易日。
  const day = date ?? asOf;
  return queryOptions({
    queryKey: qk.premarket(market, asOf, date),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/market/premarket', {
          params: { path: { market }, query: day !== undefined ? { date: day } : {} },
          signal,
        })
      ),
    staleTime: 5 * 60 * 1000,
  });
}
export function actionRadarQuery(market: Market, asOf: string | undefined, type?: string) {
  return queryOptions({
    queryKey: qk.actionRadar(market, asOf, type),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/market/action-radar', {
          params: {
            path: { market },
            query: {
              ...(asOf !== undefined ? { date: asOf } : {}),
              ...(type ? { type } : {}),
            },
          },
          signal,
        })
      ),
    staleTime: 60 * 1000,
  });
}
export type InstitutionalRankOut = components['schemas']['InstitutionalRankOut'];
export type InstitutionalRankItemOut = components['schemas']['InstitutionalRankItemOut'];
export function institutionalRankQuery(
  market: Market,
  asOf: string | undefined,
  options?: {
    investor?: 'foreign' | 'trust' | 'all';
    view?: 'amount' | 'streak' | 'sum5';
    date?: string;
    limit?: number;
  },
) {
  const investor = options?.investor ?? 'foreign';
  const view = options?.view ?? 'amount';
  const day = options?.date ?? asOf;
  return queryOptions({
    queryKey: qk.institutionalRank(market, asOf, investor, view, options?.date),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/market/institutional-rank', {
          params: {
            path: { market },
            query: {
              investor,
              view,
              ...(day !== undefined ? { date: day } : {}),
              ...(options?.limit !== undefined ? { limit: options.limit } : {}),
            },
          },
          signal,
        }),
      ),
    staleTime: 60 * 1000,
  });
}
export type KeyPriceLevelsOut = components['schemas']['KeyPriceLevelsOut'];
export function keyPriceLevelsQuery(
  market: Market,
  symbol: string,
  asOf: string | undefined,
  enabled = true,
) {
  return queryOptions({
    queryKey: qk.keyPriceLevels(market, symbol, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/securities/{symbol}/key-levels', {
          params: {
            path: { market, symbol },
            query: asOf !== undefined ? { as_of: asOf } : {},
          },
          signal,
        }),
      ),
    enabled,
    staleTime: 60 * 1000,
  });
}




export function registryQuery(market: Market, asOf: string | undefined, today: string) {
  return queryOptions({
    queryKey: qk.registry(market, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/meta/metrics', {
          params: { query: { market } },
          signal,
        }),
      ),
    // registry 是這個 app 的 schema（§10.2 規則 4）：抓一次，只有 build hash 改變才失效。
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    meta: { today },
  });
}

export type OhlcvInterval = '60m' | '1d' | '1wk' | '1mo';

export function ohlcvQuery(
  market: Market,
  symbol: string,
  range: DateRange,
  mode: AdjustMode,
  asOf: string | undefined,
  today: string,
  indicators: readonly string[] = [],
  overlays: readonly string[] = [],
  interval: OhlcvInterval = '1d',
) {
  return queryOptions({
    queryKey: qk.ohlcv(market, symbol, range, mode, asOf, indicators, overlays, interval),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/securities/{symbol}/ohlcv', {
          params: {
            path: { market, symbol },
            query: {
              from: range.from,
              to: range.to,
              mode,
              interval,
              // 指標一律由後端從 mkt.indicator_daily 取，與 K 線同一個 factor_vintage
              // （公理二）。瀏覽器**永遠**不算指標。
              ...(indicators.length === 0 ? {} : { indicators: indicators.join(',') }),
              // 籌碼疊圖走**同一個請求**。這支端點已經回一個建好的 `panes[]`
              // （label、histogram/line、zero_baseline、unit、compact_style 都在裡面），
              // 所以再打一次 /series/{metric_key} 是白跑一趟，而且會讓 label 與
              // zero-baseline 在前端被重推一次 —— 兩份定義遲早會分歧。
              ...(overlays.length === 0 ? {} : { overlays: overlays.join(',') }),
              ...asOfParam(asOf),
            },
          },
          signal,
        }),
      ),
    staleTime: staleTimeFor(asOf, today, 'daily'),
    gcTime: gcTimeFor(asOf, today),
    retry: 0,
  });
}

/**
 * `period_type` —— **顯式的查詢參數，端點的預設是 `Q`**（ROADMAP §6）。
 *
 * `fin.statement_fact` 的主鍵允許同一個 `period_end` 同時有 Q（2020 Q4）與
 * Y（FY2020）兩列。不指定就等於讓「後宣告的那一列」贏，季序列裡因此混進一個
 * 四倍的年度數字——看起來像基本面爆發，而且沒有任何錯誤訊息。
 *
 * ⚠️ **只有來自 `fin.statement_*` 的指標吃得下它**；傳給其他指標會拿到 400
 * （`period_type_not_applicable`），而那是刻意的：一個被安靜忽略的 `period_type=Y`
 * 會讓呼叫端相信自己拿到了年度數字。呼叫端請先用
 * `features/metric-studio/studio-metric.ts` 的 `isPeriodTyped()` 判斷。
 */
export type PeriodType = 'Q' | 'Y' | 'TTM';

/**
 * `/series` 的兩個「改變 payload 但不改變資源身分」的選項。
 *
 * 它們**必須同時進 query key 與 query string**——這正是 queries.ts 存在的理由
 * （見檔首）：key 帶了、請求忘了送（或反過來）都編譯得過、跑得動、畫面正常。
 */
export interface SeriesOptions {
  /** 季／年切換。省略＝不送這個參數（後端用它自己的預設 `Q`）。 */
  periodType?: PeriodType | undefined;
  /**
   * `?with=` 的疊圖 metric_key。省略＝由 registry 的 `studio_overlay` 決定；
   * 傳 `'none'` 代表明確不要疊圖。
   */
  overlay?: string | undefined;
  /**
   * 這個查詢現在要不要發。
   *
   * **它刻意不進 query key**（`enabled: false` 不是另一份資料，是「先別問」）。
   * 它存在的理由是 Metric Studio 一個元件同時掛著個股與大盤兩支端點：
   * React 的 hook 不能寫在 if 裡面，所以兩個 `useQuery` 都得呼叫，
   * 由 grain 決定哪一個真的出發。
   *
   * ⚠️ 那為什麼不在呼叫端展開成一個物件字面值再加上 enabled？因為
   * asof-contract.test.ts 規則 2 認的是「hook 的第一個參數是一個 factory 呼叫」
   * 這個**字面形狀**；第一個參數一旦變成 `{` 開頭，那條靜態檢查就整條靜悄悄地
   * 不適用了——而它正是「每一個查詢都帶 as_of」唯一的機械保證。
   * 所以 enabled 必須是 factory 的參數。
   */
  enabled?: boolean | undefined;
}

function seriesOptionParams(options: SeriesOptions): Record<string, string> {
  const out: Record<string, string> = {};
  if (options.periodType !== undefined) out['period_type'] = options.periodType;
  if (options.overlay !== undefined) out['with'] = options.overlay;
  return out;
}

export function seriesQuery(
  market: Market,
  symbol: string,
  metricKey: string,
  range: DateRange,
  asOf: string | undefined,
  today: string,
  freq: Freq | undefined,
  options: SeriesOptions = {},
) {
  return queryOptions({
    queryKey: qk.series(market, symbol, metricKey, range, asOf, options.periodType, options.overlay),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/securities/{symbol}/series/{metric_key}', {
          params: {
            path: { market, symbol, metric_key: metricKey },
            query: {
              from: range.from,
              to: range.to,
              ...asOfParam(asOf),
              ...seriesOptionParams(options),
            },
          },
          signal,
        }),
      ),
    staleTime: staleTimeFor(asOf, today, freq),
    gcTime: gcTimeFor(asOf, today),
    retry: 0,
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
  });
}

/**
 * `grain='market'` 的同一個 Metric Studio（`/{market}/market/series/{metric_key}`）。
 *
 * 後端是**同一支 handler**（§11.3），所以前端也是同一個頁面元件；分成兩個 factory
 * 只是因為路徑不同（大盤序列沒有 symbol）。回傳型別 `SeriesOut` 完全相同，
 * 差別只在 `security` 為 null。
 */
export function marketSeriesQuery(
  market: Market,
  metricKey: string,
  range: DateRange,
  asOf: string | undefined,
  today: string,
  freq: Freq | undefined,
  options: SeriesOptions = {},
) {
  return queryOptions({
    queryKey: qk.marketSeries(market, metricKey, range, asOf, options.periodType, options.overlay),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/market/series/{metric_key}', {
          params: {
            path: { market, metric_key: metricKey },
            query: {
              from: range.from,
              to: range.to,
              ...asOfParam(asOf),
              ...seriesOptionParams(options),
            },
          },
          signal,
        }),
      ),
    staleTime: staleTimeFor(asOf, today, freq),
    gcTime: gcTimeFor(asOf, today),
    retry: 0,
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
  });
}

/* ===========================================================================
 *  P2 —— 儀表板的三個呼叫（ARCHITECTURE §11.2）＋ 兩個目錄型查詢。
 *
 *  §10.5 開頭那句話值得逐字重讀：「KPI 刻意合併為一個呼叫 —— 冷啟動時四個平行
 *  請求是 4 倍的 TLS/auth 開銷，而這四張卡永遠一起渲染。」
 *  所以下面**沒有** kpiQuery(metricKey)，只有 kpisQuery(symbol)。
 * ======================================================================== */

export type SecurityDetailOut = components['schemas']['SecurityDetailOut'];
export type KpiCardsOut = components['schemas']['KpiCardsOut'];
export type KpiItemOut = components['schemas']['KpiItemOut'];
export type SymbolSearchOut = components['schemas']['SymbolSearchOut'];
export type SymbolHitOut = components['schemas']['SymbolHitOut'];
export type IndicatorCatalogOut = components['schemas']['IndicatorCatalogOut'];
export type IndicatorDefOut = components['schemas']['IndicatorDefOut'];

/** KPI 區間條的回看窗口。儀表板固定 1y（§10.5.1 的「1 年區間條」）。 */
export type KpiWindow = '1m' | '3m' | '6m' | 'ytd' | '1y' | '2y' | '3y' | '5y' | '10y' | 'max';

export function securityQuery(
  market: Market,
  symbol: string,
  asOf: string | undefined,
  today: string,
) {
  return queryOptions({
    queryKey: qk.security(market, symbol, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/securities/{symbol}', {
          params: { path: { market, symbol }, query: { ...asOfParam(asOf) } },
          signal,
        }),
      ),
    staleTime: staleTimeFor(asOf, today, 'daily'),
    gcTime: gcTimeFor(asOf, today),
    retry: 0,
  });
}

export function kpisQuery(
  market: Market,
  symbol: string,
  window: KpiWindow,
  metrics: readonly string[] | undefined,
  asOf: string | undefined,
  today: string,
) {
  return queryOptions({
    queryKey: qk.kpis(market, symbol, window, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/securities/{symbol}/kpis', {
          params: {
            path: { market, symbol },
            query: {
              window,
              ...(metrics === undefined || metrics.length === 0
                ? {}
                : { metrics: metrics.join(',') }),
              ...asOfParam(asOf),
            },
          },
          signal,
        }),
      ),
    // 四張卡裡最慢的那一個 freq 決定整組的 staleTime —— 用 daily 會讓週更的
    // 千張大戶每五分鐘被重抓一次，那是在對後端說謊說它可能變了。
    staleTime: staleTimeFor(asOf, today, 'weekly'),
    gcTime: gcTimeFor(asOf, today),
    retry: 0,
  });
}

/**
 * 「設定指標」抽屜的內容。**前端沒有任何一份寫死的指標清單**——
 * 那份清單如果寫在前端，它與 `mkt.indicator_daily` 實際算了哪些欄就會開始漂移，
 * 而漂移的症狀是「打開開關但線畫不出來」，看起來像圖表壞了。
 */
export function indicatorsQuery(market: Market, asOf: string | undefined) {
  return queryOptions({
    // market 仍然進 key（見 query-keys.ts），但**不進 query string**：
    // 目錄是全市場共用的一份設定，後端沒有這個參數。key 帶著它是為了
    // 讓 P9 真的分市場時不需要動快取形狀。
    queryKey: qk.indicators(market, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/meta/indicators', {
          params: { query: { ...asOfParam(asOf) } },
          signal,
        }),
      ),
    // 與 registry 同級：這是 app 的 schema，不是資料。
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: 0,
  });
}

/**
 * SymbolPicker 的查詢。`enabled` 由呼叫端依輸入長度決定——
 * 空字串不該打到後端，那是 11,000 列的全表掃描。
 */
/**
 * **`market` 是必填的。** 端點文件逐字：「不是選配的全市場搜尋 —— 兩條索引的
 * 第一欄都是 market，拿掉它會讓兩條路徑同時退化成全表掃描。要跨市場請打兩次。」
 * 所以這裡不提供 `market: undefined`；SymbolPicker 想跨市場就發兩個 query。
 */
export function symbolSearchQuery(
  market: Market,
  q: string,
  asOf: string | undefined,
  limit = 10,
) {
  return queryOptions({
    queryKey: qk.symbolSearch(market, q, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/symbols/search', {
          params: { query: { q, limit, market, ...asOfParam(asOf) } },
          signal,
        }),
      ),
    // 同一個前綴在 60 秒內不會有新答案；使用者刪一個字再打回去不該再打一次後端。
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 0,
  });
}

export type DigestOut = components['schemas']['DigestOut'];
export type DigestItemOut = components['schemas']['DigestItemOut'];

/**
 * 盤前摘要（【畫面 2】）。
 *
 * `date` 是後端唯一的時間參數：摘要是前一晚 23:00 寫死在 `app.digest_items` 的
 * 結論，不是一次 PIT 讀取，所以端點**不收 `?as_of=`**。呼叫端負責把時光機的位置
 * 翻譯成「要看哪一天的摘要」（`routes/$market/digest.tsx`），而 `asOf` 仍然進
 * query key —— 見 `query-keys.ts` 的 `digest`。
 *
 * `retry: 0`：未登入時這支端點回 401，而 401 是這個產品的**正常狀態**
 * （§15.1 沒有登入牆）。重試三次只會讓「請先登入」慢三倍出現。
 */
export function digestQuery(
  asOf: string | undefined,
  today: string,
  date: string | undefined = undefined,
) {
  return queryOptions({
    queryKey: qk.digest(asOf, date),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/me/digest', {
          params: { query: date === undefined ? {} : { date } },
          headers: { 'X-Requested-With': 'fetch' },
          signal,
        }),
      ),
    // 摘要一天只變一次（23:00 的 digest_build），所以它的新鮮度跟著 daily 走。
    // 倒帶到過去時 staleTimeFor 會回 Infinity —— 一份過去的摘要在構造上不可變。
    staleTime: staleTimeFor(asOf, today, 'daily'),
    gcTime: gcTimeFor(asOf, today),
    retry: 0,
  });
}

/* ===========================================================================
 *  使用者偏好（`app.user_prefs`）—— 兩個**不是** *Query factory 的函式。
 *
 *  它們刻意不叫 `prefsQuery`，也刻意不經過 `queryOptions`，因為偏好**不是
 *  時序資料**：它沒有 as_of，倒帶到 2015 年不該讓使用者的指標設定跟著退回去。
 *  硬把它塞進 as_of 契約裡，只會讓那個契約多一個「這個例外不算」的註腳，
 *  而註腳正是契約開始腐爛的地方。
 *
 *  但 asof-contract.test.ts 的規則 1 仍然成立而且仍然適用：
 *  **只有這個檔案可以碰 API client。** 所以它們住在這裡。
 * ======================================================================== */

export type PrefsOut = components['schemas']['PrefsOut'];
export type PrefsUpdate = components['schemas']['PrefsUpdate'];

/** 未登入時回 null（401 不是錯誤，見 lib/prefs/use-prefs-sync.ts 的規則 1）。 */
export async function fetchUserPrefs(signal?: AbortSignal): Promise<PrefsOut | null> {
  const result = await api.GET('/api/v1/me/prefs', signal === undefined ? {} : { signal });
  return result.data ?? null;
}

/**
 * 寫回偏好。§12.2 的 CSRF 姿態：`SameSite=Lax` + 變更型路由要求
 * `X-Requested-With` —— 少了這個 header 後端會拒絕，而那是刻意的。
 *
 * **失敗會 throw（`ApiError`）。** 這一行以前不是這樣：它只 `await api.PUT(...)`
 * 而把回傳的 `{ data, error, response }` 整個丟掉，而 openapi-fetch **不會**為
 * HTTP 錯誤拋例外 —— 於是 401 / 429 / 500 全部被當成成功。
 *
 * 那個 bug 的形狀：使用者在 session 剛好過期的那一刻改了範圍或指標，寫入被丟掉，
 * 兩邊都沒有任何訊號（沒有 console、沒有 UI 狀態、沒有重試、後端也沒有 log），
 * 而抽屜上寫著「登入後會跟著帳號走」。下次登入偏好默默退回上一次真的寫成功的那一版，
 * 事後沒有人查得出來發生過這件事（ROADMAP §5 驗收 #4）。
 *
 * 呼叫端（`lib/prefs/use-prefs-sync.ts`）負責分辨「未登入」與「真的壞了」：
 * 401 / 403 是這個產品的**正常狀態**（§15.1 沒有登入牆），其餘要吵。
 */
export async function putUserPrefs(body: PrefsUpdate): Promise<void> {
  unwrap(
    await api.PUT('/api/v1/me/prefs', {
      body,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }),
  );
}

/* ===========================================================================
 *  選股器（`/{market}/screener/*`，ADR-0002 A-D2）。
 *
 *  `run` 是 POST（body 是一份條件 DSL）但語意是讀取，所以走 queryOptions 而不是
 *  mutation：同一份條件＋同一個 as_of 就是同一份快取，按兩次「篩選」不會打兩次 API。
 *  兩者都**不進硬碟**（`lib/query-persist.ts`）：回應依工作階段而異。
 * ======================================================================== */

export type ScreenerFieldsOut = components['schemas']['ScreenerFieldsOut'];
export type ScreenerFieldOut = components['schemas']['ScreenerFieldOut'];
export type ScreenerRunIn = components['schemas']['ScreenerRunIn'];
export type ScreenerRunOut = components['schemas']['ScreenerRunOut'];
export type ScreenerRowOut = components['schemas']['ScreenerRowOut'];
export type ScreenerFilterIn = components['schemas']['ScreenerFilterIn'];

/* ===========================================================================
 *  券商分點（`/tw/securities/{symbol}/broker-branches`）。
 *
 *  端點只收 `?date=`（**精確**交易日，非交易日回空表），不收 `?as_of=`。
 *  所以這裡不把時光機的位置翻成 `date`——那會讓倒帶到週末的畫面整頁空白；
 *  `asOf` 仍然進 query key，倒帶時至少不會和「今天」共用一份快取。
 * ======================================================================== */

export type BrokerBranchesOut = components['schemas']['BrokerBranchesResponse'];

export function brokerBranchesQuery(
  symbol: string,
  asOf: string | undefined,
  today: string,
  date: string | undefined = undefined,
) {
  return queryOptions({
    queryKey: qk.brokerBranches(symbol, asOf, date),
    queryFn: async ({ signal }) => {
      const { data, response } = await api.GET(
        '/api/v1/tw/securities/{symbol}/broker-branches',
        { params: { path: { symbol }, query: date === undefined ? {} : { date } }, signal },
      );
      if (!response.ok || data === undefined) {
        throw new ApiError(
          `無法讀取券商分點籌碼: ${response.status}`,
          response.status,
          response.url,
        );
      }
      return data;
    },
    staleTime: staleTimeFor(asOf, today, 'daily'),
    gcTime: gcTimeFor(asOf, today),
  });
}

/* ===========================================================================
 *  模型輸出（`/{market}/securities/{symbol}/prediction`、`/ai/model-health`）。
 *
 *  兩支端點都受 MODEL_OUTPUT_ACCESS 閘控（ADR-0002）：閘關著（或 allowlist 而這個
 *  工作階段不在名單內）時回 **404 `model_output_not_available`**，不洩漏「這裡其實有分數」。
 *  個股端點另外有一種 404：`no_prediction`——閘開著，但這一天沒有冠軍模型的分數。
 *
 *  這兩種 404 在畫面上是兩個完全不同的狀態（「此功能未開放」vs「還沒有分數」），
 *  而且兩者都是**正常狀態**，不是錯誤。所以它們在這裡被翻成回傳值而不是例外——
 *  否則每一個呼叫端都要自己去拆 `detail.code`，而其中一個會把「未開放」畫成「沒有分數」。
 * ======================================================================== */

export type PredictionOut = components['schemas']['PredictionOut'];
export type PredictionDriverOut = components['schemas']['DriverOut'];
export type ModelHealthOut = components['schemas']['ModelHealthOut'];
export type BaselineOut = components['schemas']['BaselineOut'];

export type PredictionResult =
  | { kind: 'prediction'; prediction: PredictionOut }
  | { kind: 'no_prediction'; messageZh: string | undefined }
  | { kind: 'not_available' };

export type ModelHealthResult =
  | { kind: 'health'; health: ModelHealthOut }
  | { kind: 'not_available' };

/** 後端的錯誤形狀是 `{ detail: { code, message_zh } }`；拿不到就是 undefined。 */
function errorDetail(error: unknown): { code: string | undefined; messageZh: string | undefined } {
  const detail = jsonErrorBody(error)?.detail;
  if (typeof detail !== 'object' || detail === null) {
    return { code: undefined, messageZh: undefined };
  }
  const { code, message_zh: messageZh } = detail as { code?: unknown; message_zh?: unknown };
  return {
    code: typeof code === 'string' ? code : undefined,
    messageZh: typeof messageZh === 'string' ? messageZh : undefined,
  };
}

export function predictionQuery(
  market: Market,
  symbol: string,
  asOf: string | undefined,
  today: string,
) {
  return queryOptions({
    queryKey: qk.prediction(market, symbol, asOf),
    queryFn: async ({ signal }): Promise<PredictionResult> => {
      const result = await api.GET('/api/v1/{market}/securities/{symbol}/prediction', {
        params: { path: { market, symbol }, query: { ...asOfParam(asOf) } },
        signal,
      });
      if (result.response.status === 404) {
        const { code, messageZh } = errorDetail(result.error);
        if (code === 'model_output_not_available') return { kind: 'not_available' };
        if (code === 'no_prediction') return { kind: 'no_prediction', messageZh };
      }
      return { kind: 'prediction', prediction: unwrap(result) };
    },
    staleTime: staleTimeFor(asOf, today, 'daily'),
    gcTime: gcTimeFor(asOf, today),
    retry: 0,
  });
}

export function modelHealthQuery(market: Market, asOf: string | undefined) {
  return queryOptions({
    queryKey: qk.modelHealth(market, asOf),
    queryFn: async ({ signal }): Promise<ModelHealthResult> => {
      const result = await api.GET('/api/v1/ai/model-health', {
        params: { query: { market } },
        signal,
      });
      if (
        result.response.status === 404 &&
        errorDetail(result.error).code === 'model_output_not_available'
      ) {
        return { kind: 'not_available' };
      }
      return { kind: 'health', health: unwrap(result) };
    },
    // live IC / PSI 由夜間的 model_monitor 寫入，一天變一次；五分鐘足以讓晉升後的畫面跟上。
    staleTime: 5 * 60_000,
    retry: 0,
  });
}

/* ===========================================================================
 *  盤前通知偏好（`/me/notifications*`）—— 跟使用者偏好一樣**不是** *Query factory：
 *  它不是時序資料，沒有 as_of。呼叫端（features/digest/LineNotificationModal.tsx）
 *  自己決定快取 key；規則 1 仍然適用，所以對 API client 的呼叫住在這裡。
 *
 *  失敗訊息沿用後端的 `detail`；拿不到（非 JSON、沒有 body）時用各自的預設句。
 * ======================================================================== */

export type NotificationPrefsOut = components['schemas']['NotificationPrefsOut'];
export type NotificationPrefsIn = components['schemas']['NotificationPrefsIn'];
export type TestPushIn = components['schemas']['TestPushIn'];

/** openapi-fetch 的 error 是解析過的 JSON；不是 JSON（或沒有 body）時就不是物件。 */
function jsonErrorBody(error: unknown): { detail?: unknown } | null {
  return typeof error === 'object' && error !== null ? (error as { detail?: unknown }) : null;
}

export async function fetchNotificationPrefs(
  signal?: AbortSignal,
): Promise<NotificationPrefsOut> {
  const { data, response } = await api.GET('/api/v1/me/notifications', {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    ...(signal === undefined ? {} : { signal }),
  });
  if (!response.ok || data === undefined) throw new Error('無法讀取通知偏好');
  return data;
}

export async function putNotificationPrefs(
  body: NotificationPrefsIn,
): Promise<NotificationPrefsOut | undefined> {
  const { data, error, response } = await api.PUT('/api/v1/me/notifications', {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    body,
  });
  if (!response.ok) throw new Error(String(jsonErrorBody(error)?.detail || '儲存失敗'));
  return data;
}

export async function sendTestLinePush(
  body: TestPushIn,
): Promise<Record<string, unknown> | undefined> {
  const { data, error, response } = await api.POST('/api/v1/me/notifications/test-line', {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    body,
  });
  if (!response.ok) {
    const err = jsonErrorBody(error);
    throw new Error(err === null ? '發送失敗' : String(err.detail || '測試推播失敗'));
  }
  return data;
}

/* ===========================================================================
 *  站內說明助理（`POST /ai/ask`，ADR-0003）—— 不是 query：單次、SSE、不快取，
 *  也不是時序資料（問題與答案都不隨 as_of 改變）。這裡只負責發出請求、把位元組串流
 *  交出去；SSE 解析與狀態在 features/assistant/。
 * ======================================================================== */

export type AskIn = components['schemas']['AskIn'];

/** 測試接縫：換掉底層 fetch 與 base URL（node 環境沒有相對 URL）。正式程式碼不傳。 */
export interface ApiTransport {
  fetch: typeof globalThis.fetch;
  baseUrl: string;
}

/** `credentials` 跟著 client（lib/api/origin.ts）：網站 same-origin、App omit。 */
export function postAssistantAsk(
  body: AskIn,
  init: { signal: AbortSignal; transport?: ApiTransport | undefined },
) {
  return api.POST('/api/v1/ai/ask', {
    body,
    parseAs: 'stream',
    signal: init.signal,
    headers: { Accept: 'text/event-stream', 'X-Requested-With': 'fetch' },
    ...init.transport,
  });
}

/**
 * `GET /ai/shap-rationale`（SSE，`data: {"chunk", "done"}`）的位元組串流，給 hooks/useSseStream。
 * 走 typed client 而不是 `EventSource`：後者帶不了 `Authorization`（App），也走不了 API_ORIGIN
 * 之外的任何設定。HTTP 錯誤（404 `no_prediction`、429）throw `ApiError`。
 */
export async function openShapRationaleStream(
  query: { security_id: number; trade_date: string },
  signal: AbortSignal,
): Promise<ReadableStream<Uint8Array>> {
  const stream = unwrap(
    await api.GET('/api/v1/ai/shap-rationale', {
      params: { query },
      parseAs: 'stream',
      signal,
      headers: { Accept: 'text/event-stream' },
    }),
  );
  if (stream === null) {
    throw new ApiError('empty_stream', 200, '/api/v1/ai/shap-rationale', 'empty_stream');
  }
  return stream;
}

/* ===========================================================================
 *  自選股（`/me/watchlist-board`、`/me/watchlist-membership`）—— Slice B。
 *
 *  兩個 factory 都帶 as_of：看板的報價是 as_of 當天的數字，倒帶時不能沿用今天的快取；
 *  成員查詢的代號解析也是以 as_of 當天的別名為準。key 以 `['stock','user',…]` 開頭，
 *  所以不進 IndexedDB（lib/query-persist.ts）——那是某一個人的資料。
 *
 *  回應會因使用者操作而變（按 ☆），所以 staleTime 固定一分鐘，不走 freq 表；
 *  ☆ 切換後由呼叫端 invalidate `['stock','user','watchlist-board', market]`。
 * ======================================================================== */

export type WatchlistBoardOut = components['schemas']['WatchlistBoardOut'];
export type WatchlistBoardItemOut = components['schemas']['WatchlistBoardItemOut'];
export type WatchlistMembershipIn = components['schemas']['WatchlistMembershipIn'];
export type WatchlistMembershipOut = components['schemas']['WatchlistMembershipOut'];

const WATCHLIST_STALE_MS = 60_000;

export function watchlistBoardQuery(market: Market, asOf: string | undefined, enabled = true) {
  return queryOptions({
    queryKey: qk.watchlistBoard(market, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/me/watchlist-board', {
          params: { query: { market, ...asOfParam(asOf) } },
          signal,
        }),
      ),
    enabled,
    staleTime: WATCHLIST_STALE_MS,
    retry: 0,
  });
}

export function watchlistMembershipQuery(
  market: Market,
  symbol: string,
  asOf: string | undefined,
  enabled: boolean,
) {
  return queryOptions({
    queryKey: qk.watchlistMembership(market, symbol, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/me/watchlist-membership', {
          params: { query: { market, symbol, ...asOfParam(asOf) } },
          signal,
        }),
      ),
    enabled,
    staleTime: WATCHLIST_STALE_MS,
    retry: 0,
  });
}

export type PriceAlertOut = components['schemas']['PriceAlertOut'];
export type PriceAlertIn = components['schemas']['PriceAlertIn'];
export type PriceAlertEventOut = components['schemas']['PriceAlertEventOut'];

export function priceAlertsQuery(asOf: string | undefined, market?: string, enabled = true) {
  return queryOptions({
    queryKey: qk.priceAlerts(asOf, market),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/me/price-alerts', {
          params: { query: market ? { market } : {} },
          signal,
        }),
      ),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function priceAlertEventsQuery(asOf: string | undefined, unreadOnly = false, enabled = true) {
  return queryOptions({
    queryKey: qk.priceAlertEvents(asOf, unreadOnly),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/me/price-alert-events', {
          params: { query: { unread_only: unreadOnly } },
          signal,
        }),
      ),
    enabled,
    staleTime: 30 * 1000,
  });
}

export async function upsertPriceAlert(payload: PriceAlertIn): Promise<PriceAlertOut> {
  return unwrap(
    await api.POST('/api/v1/me/price-alerts', {
      body: payload,
    }),
  );
}

export async function deletePriceAlert(securityId: number): Promise<void> {
  unwrap(
    await api.DELETE('/api/v1/me/price-alerts/{security_id}', {
      params: { path: { security_id: securityId } },
    }),
  );
}

export async function markPriceAlertEventRead(eventId: string): Promise<void> {
  unwrap(
    await api.POST('/api/v1/me/price-alert-events/{event_id}/read', {
      params: { path: { event_id: eventId } },
    }),
  );
}

export async function markAllPriceAlertEventsRead(): Promise<void> {
  unwrap(
    await api.POST('/api/v1/me/price-alert-events/read-all'),
  );
}

/** 加入 / 移出預設清單。失敗會 throw（`ApiError`）；變更型請求帶 `X-Requested-With`。 */
export async function setWatchlistMembership(
  body: WatchlistMembershipIn,
  asOf: string | undefined,
): Promise<WatchlistMembershipOut> {
  return unwrap(
    await api.PUT('/api/v1/me/watchlist-membership', {
      body,
      params: { query: { ...asOfParam(asOf) } },
      headers: { 'X-Requested-With': 'fetch' },
    }),
  );
}

/* ===========================================================================
 *  認證（`/me`、`/captcha`、`/auth/*`）—— Slice A。
 *
 *  `meQuery()` 是**唯一**不帶 as_of 的 factory（「我是誰」不是時序資料；豁免寫在
 *  query-keys.ts 與 asof-contract.test.ts 裡）。未登入是正常狀態，不是錯誤：401 回 `null`。
 *
 *  其餘都是一次性的變更，不是 query：不快取、失敗 throw `ApiError`（`code` 帶著
 *  `captcha_invalid` / `invalid_credentials` / `reset_token_invalid` …），而且全部帶
 *  `X-Requested-With`（CSRF，§12.1）。
 * ======================================================================== */

export type MeOut = components['schemas']['MeOut'];
export type CaptchaOut = components['schemas']['CaptchaOut'];
export type SessionOut = components['schemas']['SessionOut'];
export type LoginRequest = components['schemas']['LoginRequest'];
export type RegisterRequest = components['schemas']['RegisterRequest'];
export type ForgotPasswordRequest = components['schemas']['ForgotPasswordRequest'];
export type ForgotPasswordOut = components['schemas']['ForgotPasswordOut'];
export type ResetPasswordRequest = components['schemas']['ResetPasswordRequest'];
export type ResetPasswordOut = components['schemas']['ResetPasswordOut'];
export type AccountDeleteIn = components['schemas']['AccountDeleteIn'];

const AUTH_HEADERS = { 'X-Requested-With': 'fetch' } as const;

export function meQuery() {
  return queryOptions({
    queryKey: qk.me(),
    queryFn: async ({ signal }): Promise<MeOut | null> => {
      const result = await api.GET('/api/v1/me', { signal });
      // 401 ＝ 沒登入（或工作階段剛被撤銷）。回 null 而不是 throw，所以也不會被重試。
      if (result.response.status === 401) return null;
      return unwrap(result);
    },
    staleTime: 5 * 60_000,
  });
}

export async function fetchCaptcha(): Promise<CaptchaOut> {
  return unwrap(await api.GET('/api/v1/captcha', { cache: 'no-store' }));
}

/**
 * App：`session_token` 存進 Keychain，之後的請求由 client 帶 `Authorization: Bearer`。
 * 網站的回應裡它恆為 null（工作階段在 HttpOnly cookie 裡），這裡什麼都不做。
 * 在回傳**之前**存好：呼叫端緊接著 invalidate 全部查詢，重抓時就已經帶著新身分。
 */
async function keepSession(session: SessionOut): Promise<SessionOut> {
  if (typeof session.session_token === 'string') await setToken(session.session_token);
  return session;
}

export async function login(body: LoginRequest): Promise<SessionOut> {
  const session = unwrap(await api.POST('/api/v1/auth/login', { body, headers: AUTH_HEADERS }));
  // App 的 MFA 挑戰 token 只留在記憶體，給 verifyTotp 的 X-Mfa-Token；網站是 null（挑戰在 cookie 裡）。
  setPendingMfaToken(session.mfa_token ?? null);
  return keepSession(session);
}

/**
 * 登入的第二步（帳號開了 TOTP）：網站的挑戰是 login 種下的 `mfa_pending` cookie；
 * App 沒有 cookie，改送 login 回的 `mfa_token`（`X-Mfa-Token`）。
 */
export async function verifyTotp(code: string): Promise<SessionOut> {
  const mfaToken = getPendingMfaToken();
  const session = unwrap(
    await api.POST('/api/v1/auth/totp/verify', {
      body: { code },
      headers: mfaToken === null ? AUTH_HEADERS : { ...AUTH_HEADERS, 'X-Mfa-Token': mfaToken },
    }),
  );
  setPendingMfaToken(null);
  return keepSession(session);
}

export type TotpStatusOut = components['schemas']['TotpStatusOut'];
export type TotpEnrollOut = components['schemas']['TotpEnrollOut'];

export function totpStatusQuery() {
  return queryOptions({
    queryKey: qk.totpStatus(),
    queryFn: async ({ signal }) => unwrap(await api.GET('/api/v1/auth/totp', { signal })),
    staleTime: 0,
  });
}

export async function enrollTotp(password: string): Promise<TotpEnrollOut> {
  return unwrap(
    await api.POST('/api/v1/auth/totp/enroll', { body: { password }, headers: AUTH_HEADERS }),
  );
}

export async function confirmTotp(code: string): Promise<TotpStatusOut> {
  return unwrap(
    await api.POST('/api/v1/auth/totp/confirm', { body: { code }, headers: AUTH_HEADERS }),
  );
}

export async function disableTotp(password: string, code: string): Promise<TotpStatusOut> {
  return unwrap(
    await api.POST('/api/v1/auth/totp/disable', { body: { password, code }, headers: AUTH_HEADERS }),
  );
}

export async function register(body: RegisterRequest): Promise<SessionOut> {
  return keepSession(
    unwrap(await api.POST('/api/v1/auth/register', { body, headers: AUTH_HEADERS })),
  );
}

/** 伺服器那一條撤銷成功與否（網路斷了），App 上的 token 都一起清掉：先送出（要帶著它），再清。 */
export async function logout(): Promise<void> {
  try {
    unwrap(await api.POST('/api/v1/auth/logout', { headers: AUTH_HEADERS }));
  } finally {
    await clearToken();
  }
}

export async function forgotPassword(body: ForgotPasswordRequest): Promise<ForgotPasswordOut> {
  return unwrap(await api.POST('/api/v1/auth/password/forgot', { body, headers: AUTH_HEADERS }));
}

export async function resetPassword(body: ResetPasswordRequest): Promise<ResetPasswordOut> {
  return unwrap(await api.POST('/api/v1/auth/password/reset', { body, headers: AUTH_HEADERS }));
}

/**
 * 刪除帳號（`DELETE /me`，204）。伺服器撤銷這個人的**全部**工作階段並刪掉帳號與其資料；
 * 成功之後 App 上 Keychain 裡那張 token 已經沒有用，一起清掉。失敗 throw `ApiError`
 * （`invalid_password` / `not_authenticated` / 422），token 留著——帳號還在。
 */
export async function deleteAccount(body: AccountDeleteIn): Promise<void> {
  const result = await api.DELETE('/api/v1/me', { body, headers: AUTH_HEADERS });
  // 204 沒有 body：成功就結束；失敗交給 unwrap 翻成 ApiError（帶 code / messageZh）。
  if (!result.response.ok) unwrap(result);
  await clearToken();
}

/* ===========================================================================
 *  庫存股（`/me/portfolio*`）—— P2。
 *
 *  交易流水帳是唯一的資料來源，部位由後端在讀取時重算；前端不算任何損益數字。
 *  key 全部以 `['stock','user',…]` 開頭（不進 IndexedDB）。持股、走勢、明細、試算都帶
 *  as_of；設定（`portfolioSettingsQuery`）是唯一的豁免，理由寫在 `qk.portfolioSettings`。
 *
 *  變更型請求全部帶 `X-Requested-With`；成功之後由呼叫端以三個前綴 invalidate
 *  （`features/portfolio/invalidate.ts`）。DELETE 回 204（沒有 body），所以不走 unwrap 的
 *  「data 必須存在」那條路。
 * ======================================================================== */

export type PortfolioOut = components['schemas']['PortfolioOut'];
export type PositionOut = components['schemas']['PositionOut'];
export type PortfolioSummaryOut = components['schemas']['PortfolioSummaryOut'];
export type PortfolioHistoryOut = components['schemas']['PortfolioHistoryOut'];
export type PortfolioTxnListOut = components['schemas']['PortfolioTxnListOut'];
export type PortfolioTxnOut = components['schemas']['PortfolioTxnOut'];
export type PortfolioTxnIn = components['schemas']['PortfolioTxnIn'];
export type PortfolioTxnPatch = components['schemas']['PortfolioTxnPatch'];
export type CostEstimateOut = components['schemas']['CostEstimateOut'];
export type PortfolioSettingsIn = components['schemas']['PortfolioSettingsIn'];
export type PortfolioSettingsOut = components['schemas']['PortfolioSettingsOut'];

/** 畫面提供的區間（後端另有 `all`，這一頁不用）。 */
export type PortfolioRange = Exclude<PortfolioHistoryOut['range'], 'all'>;
/** 使用者可以登錄的四種交易。 */
export type PortfolioTxnKind = PortfolioTxnIn['kind'];
/** 明細裡會出現的種類：使用者的四種＋系統列的公司行動（配股、減資…）。 */
export type PortfolioLedgerKind = PortfolioTxnOut['kind'];

export interface CostEstimateParams {
  market: Market;
  symbol: string;
  side: 'buy' | 'sell';
  tradeDate: string;
  /** 一律是「股」（張已在表單換算過）。 */
  shares: number;
  price: number;
  isDaytrade: boolean;
}

/** 使用者的操作會改變回應（新增一筆交易），所以與自選股一樣固定一分鐘，不走 freq 表。 */
const PORTFOLIO_STALE_MS = 60_000;
const PORTFOLIO_TXN_PAGE = 100;
const PORTFOLIO_MUTATION_HEADERS = { 'X-Requested-With': 'fetch' } as const;

export function portfolioQuery(market: Market, asOf: string | undefined, enabled = true) {
  return queryOptions({
    queryKey: qk.portfolio(market, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/me/portfolio', {
          params: { query: { market, ...asOfParam(asOf) } },
          signal,
        }),
      ),
    enabled,
    staleTime: PORTFOLIO_STALE_MS,
    retry: 0,
  });
}

export function portfolioHistoryQuery(
  market: Market,
  range: PortfolioRange,
  asOf: string | undefined,
  enabled = true,
) {
  return queryOptions({
    queryKey: qk.portfolioHistory(market, range, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/me/portfolio/history', {
          params: { query: { market, range, ...asOfParam(asOf) } },
          signal,
        }),
      ),
    enabled,
    staleTime: PORTFOLIO_STALE_MS,
    retry: 0,
  });
}

/**
 * 交易明細，新到舊分頁（`before` 是日期、不含當天；一頁不會切開同一個交易日）。
 * 端點不收 as_of：倒帶時第一頁從 `as_of 的隔天` 往前讀，於是看不到 as_of 之後的交易。
 */
export function portfolioTxnsQuery(
  market: Market,
  symbol: string | undefined,
  asOf: string | undefined,
  enabled = true,
) {
  const firstBefore: string | undefined = asOf === undefined ? undefined : shiftDays(asOf, 1);
  return infiniteQueryOptions({
    queryKey: qk.portfolioTxns(market, symbol, asOf),
    queryFn: async ({ signal, pageParam }) =>
      unwrap(
        await api.GET('/api/v1/me/portfolio/transactions', {
          params: {
            query: {
              market,
              limit: PORTFOLIO_TXN_PAGE,
              ...(symbol === undefined ? {} : { symbol }),
              ...(pageParam === undefined ? {} : { before: pageParam }),
            },
          },
          signal,
        }),
      ),
    initialPageParam: firstBefore,
    getNextPageParam: (last: PortfolioTxnListOut) => last.next_before ?? undefined,
    enabled,
    staleTime: PORTFOLIO_STALE_MS,
    retry: 0,
  });
}

/** 手續費／稅額試算。表單每改一次（debounce 後）就是一個新 key。 */
export function costEstimateQuery(
  params: CostEstimateParams,
  asOf: string | undefined,
  enabled: boolean,
) {
  return queryOptions({
    queryKey: qk.portfolioCostEstimate(params, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/me/portfolio/cost-estimate', {
          params: {
            query: {
              market: params.market,
              symbol: params.symbol,
              side: params.side,
              trade_date: params.tradeDate,
              shares: params.shares,
              price: params.price,
              is_daytrade: params.isDaytrade,
            },
          },
          signal,
        }),
      ),
    enabled,
    staleTime: 10 * 60_000,
    retry: 0,
  });
}

/** 設定（as_of 豁免，見 `qk.portfolioSettings`）。 */
export function portfolioSettingsQuery(enabled = true) {
  return queryOptions({
    queryKey: qk.portfolioSettings(),
    queryFn: async ({ signal }) =>
      unwrap(await api.GET('/api/v1/me/portfolio/settings', { signal })),
    enabled,
    staleTime: 5 * 60_000,
    retry: 0,
  });
}

export async function createPortfolioTxn(body: PortfolioTxnIn): Promise<PortfolioTxnOut> {
  return unwrap(
    await api.POST('/api/v1/me/portfolio/transactions', {
      body,
      headers: PORTFOLIO_MUTATION_HEADERS,
    }),
  );
}

export async function updatePortfolioTxn(
  txnId: string,
  body: PortfolioTxnPatch,
): Promise<PortfolioTxnOut> {
  return unwrap(
    await api.PATCH('/api/v1/me/portfolio/transactions/{txn_id}', {
      params: { path: { txn_id: txnId } },
      body,
      headers: PORTFOLIO_MUTATION_HEADERS,
    }),
  );
}

export async function deletePortfolioTxn(txnId: string): Promise<void> {
  const result = await api.DELETE('/api/v1/me/portfolio/transactions/{txn_id}', {
    params: { path: { txn_id: txnId } },
    headers: PORTFOLIO_MUTATION_HEADERS,
  });
  // 204 沒有 body：成功就結束；失敗交給 unwrap 翻成 ApiError（帶 code / messageZh）。
  if (!result.response.ok) unwrap(result);
}

export async function putPortfolioSettings(
  body: PortfolioSettingsIn,
): Promise<PortfolioSettingsOut> {
  return unwrap(
    await api.PUT('/api/v1/me/portfolio/settings', {
      body,
      headers: PORTFOLIO_MUTATION_HEADERS,
    }),
  );
}

/* -----------------------------------------------------------------------------
 *  P12.6 內部研究頁與後台。兩者對沒有權限的帳號都回 404（不洩漏存在）。
 * -------------------------------------------------------------------------- */
export type ResearchWatchOut = components['schemas']['ResearchWatchOut'];
export type ResearchRowOut = components['schemas']['ResearchRowOut'];
export type MaLevelOut = components['schemas']['MaLevelOut'];
export type AdminUserOut = components['schemas']['AdminUserOut'];

export function researchWatchQuery(market: Market, asOf: string | undefined, enabled = true) {
  return queryOptions({
    queryKey: qk.researchWatch(market, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/research/watch', {
          params: {
            path: { market },
            query: asOf !== undefined ? { as_of: asOf } : {},
          },
          signal,
        }),
      ),
    enabled,
    retry: false,
    staleTime: 60 * 1000,
  });
}

export function adminUsersQuery(asOf: string | undefined, q: string, enabled = true) {
  return queryOptions({
    queryKey: qk.adminUsers(asOf, q),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/admin/users', {
          params: { query: { q, limit: 100 } },
          signal,
        }),
      ),
    enabled,
    retry: false,
    staleTime: 0,
  });
}

export type AdminAuditOut = components['schemas']['AdminAuditOut'];

export function adminAuditQuery(asOf: string | undefined, enabled = true) {
  return queryOptions({
    queryKey: qk.adminAudit(asOf),
    queryFn: async ({ signal }) =>
      unwrap(await api.GET('/api/v1/admin/audit-log', { params: { query: { limit: 50 } }, signal })),
    enabled,
    retry: false,
    staleTime: 0,
  });
}

export async function grantEntitlement(
  userId: string,
  entitlement: 'admin' | 'research',
  note: string,
): Promise<AdminUserOut> {
  return unwrap(
    await api.POST('/api/v1/admin/users/{user_id}/entitlements', {
      params: { path: { user_id: userId } },
      body: { entitlement, note },
    }),
  );
}

export async function revokeEntitlement(
  userId: string,
  entitlement: 'admin' | 'research',
  note: string,
): Promise<void> {
  unwrap(
    await api.DELETE('/api/v1/admin/users/{user_id}/entitlements/{entitlement}', {
      params: { path: { user_id: userId, entitlement }, query: { note } },
    }),
  );
}

export type HolderDistributionOut = components['schemas']['HolderDistributionOut'];
export type HolderLevelOut = components['schemas']['HolderLevelOut'];
export type StockAnalysisOut = components['schemas']['StockAnalysisOut'];
export type AnalysisFactorOut = components['schemas']['AnalysisFactorOut'];

export function holderDistributionQuery(
  market: Market,
  symbol: string,
  asOf: string | undefined,
  enabled = true,
) {
  return queryOptions({
    queryKey: qk.holderDistribution(market, symbol, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/securities/{symbol}/holder-distribution', {
          params: { path: { market, symbol }, query: { ...asOfParam(asOf) } },
          signal,
        }),
      ),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

export function stockAnalysisQuery(
  market: Market,
  symbol: string,
  asOf: string | undefined,
  enabled = true,
) {
  return queryOptions({
    queryKey: qk.stockAnalysis(market, symbol, asOf),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET('/api/v1/{market}/securities/{symbol}/analysis', {
          params: { path: { market, symbol }, query: { ...asOfParam(asOf) } },
          signal,
        }),
      ),
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}
